import { ChangeDetectorRef, Component, computed, EventEmitter, Input, OnChanges, Output, signal, SimpleChanges, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import {
    NgxDaterangepickerBootstrapComponent,
    NgxDaterangepickerBootstrapDirective,
} from 'ngx-daterangepicker-bootstrap';

import * as Types from '../../shared/types';
import dayjs, { Dayjs } from 'dayjs';
import * as Constants from '../../shared/constant';
import { SharedDataService } from '../../services/shared-data.service';
import { CommonService } from '../../services/common.service';
import { ButtonComponent } from '../button/button.component';
import { TruncatePipe } from '../../pipes/truncate.pipe';
import { OutsideClickDirective } from '../../directives/outside-click.directive';
import { DateRangePickerService } from './date-range-picker.service';
import { Subscription } from 'rxjs';

export interface DateObject extends Object {
  startDate: dayjs.Dayjs;
  endDate: dayjs.Dayjs;
  label: string;
}

@Component({
  selector: 'date-range-picker',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatInputModule,
    NgxDaterangepickerBootstrapDirective,
    NgxDaterangepickerBootstrapComponent,
    ButtonComponent,
    TruncatePipe,
    OutsideClickDirective,
  ],
  templateUrl: './date-range-picker.component.html',
  styleUrl: './date-range-picker.component.scss',
})
export class DaterangePickerComponent implements OnChanges {
  constructor(
    private cdr: ChangeDetectorRef,
    public sharedDataService: SharedDataService,
    public commonService: CommonService,
    private dateRangePickerService: DateRangePickerService
  ) {}

  @ViewChild(NgxDaterangepickerBootstrapDirective, { static: false }) pickerDirective:
    | NgxDaterangepickerBootstrapDirective
    | undefined;
  @Input() customFormat: string = 'YYYY-MM-DD';
  /** Calendar opens above or below the trigger (`down` everywhere by default). */
  @Input() calendarDrops: 'down' | 'up' = 'down';
  /** Horizontal alignment of the popup relative to the trigger. */
  @Input() calendarOpens: 'left' | 'right' | 'center' = 'left';
  /** When set (e.g. 'base' | 'comparison'), this picker uses isolated state instead of the global service. */
  @Input() pickerContext: string | null = null;
  @Input() initialStartDate: string | null = null;
  @Input() initialEndDate: string | null = null;
  @Output() onDateSelect = new EventEmitter<DateObject>();

  private toggleValuesSubscription!: Subscription;
  id = Math.random();
  isInitialized = false;
  divWidthTop: string = '70px'; // Default width
  divWidthBottom: string = '65px'; // Default width
  protected _dropdownOpened = signal<boolean>(false);
  dropsDown = 'down';
  dropsUp = 'up';
  opensRight = 'right';
  opensCenter = 'center';
  opensLeft = 'left';
  selectedRangeCalendar = computed<Types.SelectedRangeCalendar>(() =>
    this.dateRangePickerService.selectedRangeCalendar()
  );
  /** Isolated state when pickerContext is set (e.g. trends base vs comparison). */
  private _localRange = signal<Types.SelectedRangeCalendar>({ ...Constants.InitialDate });
  /** Display value: local state when pickerContext set, otherwise global service. */
  displayRange = computed<Types.SelectedRangeCalendar>(() =>
    this.pickerContext ? this._localRange() : this.selectedRangeCalendar()
  );
  maxDate?: Dayjs;
  minDate?: Dayjs;
  invalidDates: Dayjs[] = [];
  startAPIDate: string | undefined;
  endAPIDate: string | undefined;
  selectedDate: string | undefined;
  referenceDate = Constants.ReferenceDate;
  ranges = Constants.Ranges;
  localeTime = Constants.LocaleTime;
  locale = Constants.Locale;
  tooltips = [
    { date: dayjs(), text: '' },
    { date: dayjs().add(2, 'days'), text: '' },
  ];

  ngOnChanges(changes: SimpleChanges) {
    if (!this.pickerContext) return;
    const start = changes['initialStartDate'] || changes['initialEndDate'];
    if (start && this.initialStartDate && this.initialEndDate) {
      const startDate = dayjs(this.initialStartDate);
      const endDate = dayjs(this.initialEndDate);
      const label = `${startDate.format('DD MMM YYYY')} - ${endDate.format('DD MMM YYYY')}`;
      this._localRange.set({ startDate, endDate, label });
      this.setWidth({ startDate, endDate, label });
    }
  }

  ngOnInit() {
    this.toggleValuesSubscription = this.sharedDataService.getToggleValues().subscribe((values) => {
      if (!values.date) {
        this._dropdownOpened.set(values.date);
        this.pickerDirective?.hide();
      }
    });

    if (this.pickerContext) {
      // Isolated mode (e.g. trends base vs comparison): use local state only
      if (this.initialStartDate && this.initialEndDate) {
        const startDate = dayjs(this.initialStartDate);
        const endDate = dayjs(this.initialEndDate);
        const label = `${startDate.format('DD MMM YYYY')} - ${endDate.format('DD MMM YYYY')}`;
        this._localRange.set({ startDate, endDate, label });
        this.setWidth({ startDate, endDate, label });
      } else {
        this.setWidth(this._localRange() as Types.DateObject);
      }
      this.isInitialized = true;
      return;
    }

    this.sharedDataService.getDashboardState().subscribe((dashboardData) => {
      this.dateRangePickerService.setSelectedRangeCalendar(dashboardData.dateRange);
      dashboardData.dateRange.startDate = dayjs(dashboardData.dateRange.startDate);
      dashboardData.dateRange.endDate = dayjs(dashboardData.dateRange.endDate);

      this.sharedDataService.setDateRangeObject({ ...dashboardData.dateRange });

      this.formatDateUpdateService(dashboardData.dateRange);
    });

    this.setWidth(this.selectedRangeCalendar() as Types.DateObject);

    if (
      !this.dateRangePickerService.dateInitialized() &&
      !this.sharedDataService.getDashboardStateValue().dateRange.startDate
    ) {
      let updatedRangeObject = {
        startDate: this.ranges['Last 7 days'][0],
        endDate: this.ranges['Last 7 days'][1],
        label: '',
      };

      this.sharedDataService.setDateRangeObject({ ...updatedRangeObject });

      this.formatDateUpdateService(updatedRangeObject);
    }

    this.isInitialized = true;
    this.dateRangePickerService.setDateInitialized(true);
  }

  ngOnDestroy() {
    if (this.toggleValuesSubscription) {
      this.toggleValuesSubscription.unsubscribe();
    }
    this.cdr.detectChanges();
  }

  isInvalidDate = (m: Dayjs) => {
    return this.invalidDates.some((d) => d.isSame(m, 'day'));
  };

  isCustomDates = (date: Dayjs) => {
    return date.month() === 0 || date.month() === 6 ? 'mycustomdate' : false;
  };

  isTooltipDate = (m: Dayjs) => {
    const tooltip = this.tooltips.find((tt) => tt.date.isSame(m, 'day'));
    return tooltip ? tooltip.text : false;
  };

  toTitleCase = (str: string) => {
    return str.replace(/\w\S*/g, (text) => text.charAt(0).toUpperCase() + text.substring(1).toLowerCase());
  };

  setWidth(selectedRangeCalendar: { startDate: dayjs.Dayjs; endDate: dayjs.Dayjs; label?: string }) {
    if (selectedRangeCalendar.label) {
      if (selectedRangeCalendar.label.length < 12) {
        this.divWidthTop = '120px';
        this.divWidthBottom = '114px';
      } else if (selectedRangeCalendar.label.length < 15) {
        this.divWidthTop = '135px';
        this.divWidthBottom = '129px';
      } else {
        this.divWidthTop = '223px';
        this.divWidthBottom = '217px';
      }
    }
  }

  formatDateUpdateService(updatedRangeObject: Types.DateObject) {
    const selectedTimezone: string = this.sharedDataService.selectedTimezoneValue;

    this.startAPIDate = updatedRangeObject.startDate.format('YYYY-MM-DD');
    this.endAPIDate = updatedRangeObject.endDate.format('YYYY-MM-DD');

    this.sharedDataService.setDateRange({
      startAPIDate: this.startAPIDate,
      endAPIDate: this.endAPIDate,
      dateSelected: true,
    });
  }

  datesUpdatedRange($event: object) {
    let updatedRangeObject = $event as DateObject;

    if (updatedRangeObject?.startDate && updatedRangeObject?.endDate) {
      const diffInDays = updatedRangeObject.endDate.diff(updatedRangeObject.startDate, 'day');

      if (diffInDays > 92) {
        alert('You can select a maximum range of 92 days only.');
        if (this.pickerDirective) {
          this.pickerDirective.hide();
        }
        return;
      }
    }

    if (this.pickerContext) {
      // Isolated mode: update local state only and emit to parent
      if (updatedRangeObject?.startDate && updatedRangeObject?.endDate) {
        let label = updatedRangeObject.label;
        if (label === 'Custom Range' || !label) {
          label = `${updatedRangeObject.startDate.format('DD MMM YYYY')} - ${updatedRangeObject.endDate.format('DD MMM YYYY')}`;
        } else {
          label = this.toTitleCase(label);
        }
        this._localRange.set({
          startDate: updatedRangeObject.startDate,
          endDate: updatedRangeObject.endDate,
          label,
        });
        this.setWidth(updatedRangeObject as Types.DateObject);
        this.onDateSelect.emit(updatedRangeObject);
      }
      this.cdr.detectChanges();
      return;
    }

    let selectedRangeCalendarTemp = { ...this.selectedRangeCalendar() };
    if (updatedRangeObject?.startDate) {
      this.dateRangePickerService.setSelectedRangeCalendar(updatedRangeObject);
      this.setWidth(updatedRangeObject as Types.DateObject);
    }

    if (updatedRangeObject?.label) {
      selectedRangeCalendarTemp.label = this.toTitleCase(updatedRangeObject.label);

      if (selectedRangeCalendarTemp.label === 'Custom Range') {
        selectedRangeCalendarTemp.label = `${updatedRangeObject.startDate.format('DD MMM YYYY')} - ${updatedRangeObject.endDate.format('DD MMM YYYY')}`;
      }

      this.sharedDataService.setDateRangeObject({ ...updatedRangeObject });

      if (updatedRangeObject?.startDate) {
        this.sharedDataService.updateDashboardState('dateRange', updatedRangeObject);
        this.formatDateUpdateService(updatedRangeObject);
      }

      this.commonService.historyPageNumber = 1;
    }
  }

  makeLoaderTrue(event: MouseEvent) {
    this.commonService.setLoaderFlag(true);
    this.cdr.detectChanges();
  }

  toggleDropdown = () => {
    if (this.pickerDirective) {
      this.pickerDirective.toggle();
    }
  };

  handleOutSideClick = () => {
    this._dropdownOpened.set(false);
    this.cdr.detectChanges();
  };

  getDateLabel = () => {
    return this.displayRange().label ?? '';
  };
}
